import React, { useState, useEffect, useCallback } from 'react';
import { Send, Users, Building2, CheckCircle, BarChart2, ClipboardList, Settings, Plus, Trash2, Eye, RefreshCw, MessageSquare, Link, Bell, Search, X, AlertCircle } from 'lucide-react';
import { API_URL } from '../config';
import { formatCurrency, formatDateTime } from '../utils/formatters';

// Status Badge Component
const StatusBadge = ({ status }) => {
  const styles = {
    sent: 'bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/30',
    pending: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/30',
    failed: 'bg-red-500/15 text-red-400 border-red-500/30',
    skipped: 'bg-orange-500/15 text-orange-400 border-orange-500/30',
    enabled: 'bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/30',
    disabled: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
    configured: 'bg-[#22c55e]/15 text-[#22c55e] border-[#22c55e]/30',
    not_configured: 'bg-zinc-500/15 text-zinc-400 border-zinc-500/30',
  };

  const icons = {
    sent: '✓',
    pending: '○',
    failed: '✕',
    skipped: '⊘',
    enabled: '●',
    disabled: '○',
    configured: '✓',
    not_configured: '○',
  };

  const labels = {
    configured: 'Configured',
    not_configured: 'Not Configured',
  };

  return (
    <span className={`${styles[status] || styles.pending} px-2.5 py-1 rounded-full text-xs font-semibold border inline-flex items-center gap-1.5`}>
      <span>{icons[status] || '○'}</span>
      {labels[status] || status}
    </span>
  );
};

// Summary Card Component
const SummaryCard = ({ title, value, subtitle, icon, color = 'cyan' }) => {
  const colors = {
    cyan: 'from-[#6366f1]/20 to-[#6366f1]/5 border-[#6366f1]/30 text-[#6366f1]',
    green: 'from-[#22c55e]/20 to-[#22c55e]/5 border-[#22c55e]/30 text-[#22c55e]',
    yellow: 'from-yellow-500/20 to-yellow-500/5 border-yellow-500/30 text-yellow-400',
    red: 'from-red-500/20 to-red-500/5 border-red-500/30 text-red-400',
    purple: 'from-purple-500/20 to-purple-500/5 border-purple-500/30 text-purple-400',
  };

  return (
    <div className={`bg-gradient-to-br ${colors[color]} rounded-xl border p-4`}>
      <div className="flex items-center justify-between mb-2">
        <span className="flex items-center justify-center">{icon}</span>
        <span className={`text-2xl font-bold font-mono`}>{value}</span>
      </div>
      <p className="text-sm text-zinc-300">{title}</p>
      {subtitle && <p className="text-xs text-zinc-500 mt-1">{subtitle}</p>}
    </div>
  );
};

// Message Preview Modal
const MessagePreviewModal = ({ log, onClose }) => {
  if (!log) return null;

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-white">Message Preview</h3>
            <p className="text-zinc-400 text-sm mt-1">Sent to {log.recipient_name}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">✕</button>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1">
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Status</span>
              <StatusBadge status={log.status} />
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Sent At</span>
              <span className="text-white">{formatDateTime(log.sent_at || log.created_at)}</span>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-zinc-400">Chat ID</span>
              <span className="text-zinc-300 font-mono">{log.recipient_chat_id}</span>
            </div>
            {log.total_spend > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Spend</span>
                <span className="text-white">{formatCurrency(log.total_spend)}</span>
              </div>
            )}
            {log.total_leads > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-zinc-400">Leads</span>
                <span className="text-[#22c55e]">{log.total_leads}</span>
              </div>
            )}
          </div>

          {/* Message Content */}
          <div className="mt-6">
            <p className="text-xs text-zinc-500 mb-2 uppercase tracking-wider">Message Content</p>
            <div className="bg-zinc-800/70 rounded-xl p-4 text-sm text-zinc-200 whitespace-pre-wrap font-mono leading-relaxed border border-white/5">
              {log.message_content || (
                <span className="text-zinc-500 italic">Message content not available for older logs</span>
              )}
            </div>
          </div>

          {log.error_message && (
            <div className="mt-4 bg-red-500/10 border border-red-500/30 rounded-lg p-3">
              <p className="text-red-400 text-sm flex items-center gap-1.5"><svg className="w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>{log.error_message}</p>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Client Card Component
const ClientCard = ({ client, onConfigure, onSend, onViewHistory }) => {
  const isConfigured = !!client.telegram_chat_id;
  
  return (
    <div className="bg-[#09090B] rounded-xl border border-white/10 hover:border-[#22c55e]/30 transition-colors p-3 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
            <h4 className="font-semibold text-white text-sm sm:text-lg truncate">{client.name}</h4>
            <StatusBadge status={isConfigured ? 'configured' : 'not_configured'} />
          </div>
          <p className="text-zinc-400 text-xs sm:text-sm mb-3 truncate">{client.company_name || client.email}</p>
          
          <div className="grid grid-cols-3 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">Accounts</p>
              <p className="text-white font-medium">{client.accounts_count}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">Frequency</p>
              <p className="text-white font-medium capitalize">{client.report_frequency}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">Last Sent</p>
              <p className="text-white font-medium text-[10px] sm:text-sm">{client.last_report_sent ? formatDateTime(client.last_report_sent) : 'Never'}</p>
            </div>
          </div>

          {/* Account Pills */}
          {client.accounts?.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {client.accounts.slice(0, 3).map(acc => (
                <span key={acc.id} className="px-2 py-0.5 bg-zinc-800 rounded text-[10px] sm:text-xs text-zinc-400 truncate max-w-[100px] sm:max-w-none">
                  [{acc.platform === 'META' ? 'Meta' : 'Google'}] {acc.name.slice(0, 15)}
                </span>
              ))}
              {client.accounts.length > 3 && (
                <span className="px-2 py-0.5 bg-zinc-800 rounded text-[10px] sm:text-xs text-zinc-500">
                  +{client.accounts.length - 3}
                </span>
              )}
            </div>
          )}
        </div>

        <div className="flex sm:flex-col gap-2 flex-wrap">
          <button
            onClick={() => onConfigure(client)}
            className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors min-h-[40px] sm:min-h-0"
          >
            Configure
          </button>
          {isConfigured && (
            <>
              <button
                onClick={() => onSend(client)}
                className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-[#22c55e]/20 text-[#22c55e] rounded-lg text-xs font-semibold hover:bg-[#22c55e]/30 transition-colors min-h-[40px] sm:min-h-0"
              >
                Send Now
              </button>
              <button
                onClick={() => onViewHistory(client)}
                className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors min-h-[40px] sm:min-h-0"
              >
                History
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Team Member Card Component
const TeamMemberCard = ({ member, onConfigure, onSend, onViewHistory }) => {
  const isConfigured = !!member.telegram_chat_id;
  
  return (
    <div className="bg-[#09090B] rounded-xl border border-white/10 hover:border-[#6366f1]/30 transition-colors p-3 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 sm:gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 sm:gap-3 mb-2 flex-wrap">
            <h4 className="font-semibold text-white text-sm sm:text-lg truncate">{member.name}</h4>
            <span className="px-2 py-0.5 bg-[#6366f1]/20 text-[#6366f1] rounded text-[10px] sm:text-xs font-medium capitalize">
              {member.role}
            </span>
            <StatusBadge status={isConfigured ? 'configured' : 'not_configured'} />
          </div>
          <p className="text-zinc-400 text-xs sm:text-sm mb-3 truncate">{member.email}</p>
          
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4 text-xs sm:text-sm">
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">Total Accounts</p>
              <p className="text-white font-medium">{member.total_accounts}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">As Marketer</p>
              <p className="text-white font-medium">{member.accounts_as_marketer}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">As Team Lead</p>
              <p className="text-white font-medium">{member.accounts_as_team_lead}</p>
            </div>
            <div>
              <p className="text-zinc-500 text-[10px] sm:text-xs">Last Sent</p>
              <p className="text-white font-medium text-[10px] sm:text-sm">{member.last_report_sent ? formatDateTime(member.last_report_sent) : 'Never'}</p>
            </div>
          </div>

          {/* Report Types */}
          {isConfigured && (
            <div className="mt-3 flex flex-wrap gap-1.5">
              {member.receive_weekly_summary && (
                <span className="px-2 py-0.5 bg-purple-500/20 text-purple-400 rounded text-[10px] sm:text-xs">Weekly</span>
              )}
              {member.receive_daily_summary && (
                <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded text-[10px] sm:text-xs">Daily</span>
              )}
              {member.receive_inactive_alerts && (
                <span className="px-2 py-0.5 bg-yellow-500/20 text-yellow-400 rounded text-[10px] sm:text-xs">Inactive</span>
              )}
              {member.receive_health_alerts && (
                <span className="px-2 py-0.5 bg-red-500/20 text-red-400 rounded text-[10px] sm:text-xs">Health</span>
              )}
            </div>
          )}
        </div>

        <div className="flex sm:flex-col gap-2 flex-wrap">
          <button
            onClick={() => onConfigure(member)}
            className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors min-h-[40px] sm:min-h-0"
          >
            Configure
          </button>
          {isConfigured && (
            <>
              <button
                onClick={() => onSend(member, 'summary')}
                className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-[#6366f1]/20 text-[#6366f1] rounded-lg text-xs font-semibold hover:bg-[#6366f1]/30 transition-colors min-h-[40px] sm:min-h-0"
              >
                Send Summary
              </button>
              <button
                onClick={() => onViewHistory(member)}
                className="flex-1 sm:flex-none px-3 py-2 sm:py-1.5 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors min-h-[40px] sm:min-h-0"
              >
                History
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Configure Client Modal
const ConfigureClientModal = ({ client, onClose, onSave }) => {
  const [chatId, setChatId] = useState(client?.telegram_chat_id || '');
  const [frequency, setFrequency] = useState(client?.report_frequency || 'weekly');
  const [receiveReports, setReceiveReports] = useState(client?.receive_reports ?? true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(client.id, chatId, frequency, receiveReports);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-md">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Configure Client Reports</h3>
          <p className="text-zinc-400 text-sm mt-1">{client.name}</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-zinc-300 mb-2">Telegram Chat ID</label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="e.g., 123456789"
              className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-[#22c55e] focus:outline-none"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Note: Client needs to message your bot with /start to get their Chat ID
            </p>
          </div>

          <div>
            <label className="block text-sm text-zinc-300 mb-2">Report Frequency</label>
            <select
              value={frequency}
              onChange={(e) => setFrequency(e.target.value)}
              className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white focus:border-[#22c55e] focus:outline-none"
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
              <option value="none">Manual Only</option>
            </select>
          </div>

          <label className="flex items-center gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={receiveReports}
              onChange={(e) => setReceiveReports(e.target.checked)}
              className="w-4 h-4 accent-[#22c55e] rounded"
            />
            <span className="text-sm text-zinc-300">Enable automatic reports</span>
          </label>

          <div className="bg-zinc-800/30 rounded-lg p-4">
            <p className="text-xs text-zinc-400 mb-2">This client has access to {client.accounts_count} account(s):</p>
            <div className="flex flex-wrap gap-1.5">
              {client.accounts?.slice(0, 5).map(acc => (
                <span key={acc.id} className="px-2 py-0.5 bg-zinc-700 rounded text-xs text-zinc-300">
                  {acc.name.slice(0, 25)}
                </span>
              ))}
              {client.accounts?.length > 5 && (
                <span className="px-2 py-0.5 bg-zinc-700 rounded text-xs text-zinc-500">
                  +{client.accounts.length - 5} more
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-[#22c55e] text-black rounded-lg text-sm font-semibold hover:bg-[#22c55e]/80 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Configure Team Member Modal
const ConfigureTeamMemberModal = ({ member, onClose, onSave }) => {
  const [chatId, setChatId] = useState(member?.telegram_chat_id || '');
  const [dailySummary, setDailySummary] = useState(member?.receive_daily_summary ?? false);
  const [inactiveAlerts, setInactiveAlerts] = useState(member?.receive_inactive_alerts ?? true);
  const [healthAlerts, setHealthAlerts] = useState(member?.receive_health_alerts ?? true);
  const [weeklySummary, setWeeklySummary] = useState(member?.receive_weekly_summary ?? true);
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    await onSave(member.id, chatId, dailySummary, inactiveAlerts, healthAlerts, weeklySummary);
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-md">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Configure Team Member</h3>
          <p className="text-zinc-400 text-sm mt-1">{member.name} • {member.role}</p>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-zinc-300 mb-2">Telegram Chat ID</label>
            <input
              type="text"
              value={chatId}
              onChange={(e) => setChatId(e.target.value)}
              placeholder="e.g., 123456789"
              className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white placeholder-zinc-500 focus:border-[#6366f1] focus:outline-none"
            />
            <p className="text-xs text-zinc-500 mt-1">
              Note: Message the bot with /start to get your Chat ID
            </p>
          </div>

          <div className="bg-zinc-800/30 rounded-xl p-4">
            <h4 className="text-sm font-semibold text-white mb-3">Report Types</h4>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={weeklySummary}
                  onChange={(e) => setWeeklySummary(e.target.checked)}
                  className="w-4 h-4 accent-[#6366f1] rounded"
                />
                <div>
                  <span className="text-sm text-zinc-300">Weekly Summary</span>
                  <p className="text-xs text-zinc-500">Performance overview of assigned accounts</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={dailySummary}
                  onChange={(e) => setDailySummary(e.target.checked)}
                  className="w-4 h-4 accent-[#6366f1] rounded"
                />
                <div>
                  <span className="text-sm text-zinc-300">Daily Summary</span>
                  <p className="text-xs text-zinc-500">Daily updates on account performance</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={inactiveAlerts}
                  onChange={(e) => setInactiveAlerts(e.target.checked)}
                  className="w-4 h-4 accent-[#6366f1] rounded"
                />
                <div>
                  <span className="text-sm text-zinc-300">Inactive Account Alerts</span>
                  <p className="text-xs text-zinc-500">When accounts stop spending</p>
                </div>
              </label>
              
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={healthAlerts}
                  onChange={(e) => setHealthAlerts(e.target.checked)}
                  className="w-4 h-4 accent-[#6366f1] rounded"
                />
                <div>
                  <span className="text-sm text-zinc-300">Health Alerts</span>
                  <p className="text-xs text-zinc-500">Critical issues requiring attention</p>
                </div>
              </label>
            </div>
          </div>

          <div className="bg-zinc-800/30 rounded-lg p-3 text-sm text-zinc-400">
            Managing {member.total_accounts} accounts ({member.accounts_as_marketer} as marketer, {member.accounts_as_team_lead} as team lead)
          </div>
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 bg-[#6366f1] text-white rounded-lg text-sm font-semibold hover:bg-[#6366f1]/80 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Send Report Modal
const SendReportModal = ({ recipient, type, onClose, onSend }) => {
  const [period, setPeriod] = useState('last_7_days');
  const [reportType, setReportType] = useState('summary');
  const [sending, setSending] = useState(false);

  const handleSend = async () => {
    setSending(true);
    if (type === 'client') {
      await onSend(recipient.id, period);
    } else {
      await onSend(recipient.id, reportType);
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-md">
        <div className="p-6 border-b border-white/10">
          <h3 className="text-xl font-bold text-white">Send Report</h3>
          <p className="text-zinc-400 text-sm mt-1">To: {recipient.name}</p>
        </div>
        
        <div className="p-6 space-y-4">
          {type === 'client' ? (
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Report Period</label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white focus:border-[#22c55e] focus:outline-none"
              >
                <option value="yesterday">Yesterday</option>
                <option value="last_7_days">Last 7 Days</option>
                <option value="last_30_days">Last 30 Days</option>
              </select>
              <div className="mt-3 bg-zinc-800/30 rounded-lg p-3">
                <p className="text-xs text-zinc-400">
                  This will send a performance report for {recipient.accounts_count} account(s) to the client's Telegram.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <label className="block text-sm text-zinc-300 mb-2">Report Type</label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-800/50 border border-white/10 rounded-lg text-white focus:border-[#6366f1] focus:outline-none"
              >
                <option value="summary">Weekly Summary</option>
                <option value="inactive_alerts">Inactive Account Alert</option>
                <option value="health_alerts">Health Alerts</option>
              </select>
              <div className="mt-3 bg-zinc-800/30 rounded-lg p-3">
                <p className="text-xs text-zinc-400">
                  This will send a report for {recipient.total_accounts} assigned account(s) to the team member's Telegram.
                </p>
              </div>
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending}
            className={`px-4 py-2 ${type === 'client' ? 'bg-[#22c55e] text-black' : 'bg-[#6366f1] text-white'} rounded-lg text-sm font-semibold hover:opacity-80 transition-colors disabled:opacity-50`}
          >
            {sending ? 'Sending...' : 'Send Now'}
          </button>
        </div>
      </div>
    </div>
  );
};

// History Modal
const HistoryModal = ({ recipient, logs, onClose, onViewMessage }) => {
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-xl flex items-center justify-center z-50 p-4">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-white/10 flex justify-between items-center">
          <div>
            <h3 className="text-xl font-bold text-white">Report History</h3>
            <p className="text-zinc-400 text-sm mt-1">{recipient.name}</p>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-white text-xl">✕</button>
        </div>
        
        <div className="p-6 overflow-y-auto flex-1">
          {logs.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-3xl mb-2">📭</p>
              <p className="text-zinc-400">No reports sent yet</p>
            </div>
          ) : (
            <div className="space-y-3">
              {logs.map(log => (
                <div 
                  key={log.id} 
                  className="flex items-center justify-between bg-zinc-800/50 rounded-lg px-4 py-3 border border-white/5 hover:border-white/10 transition-colors cursor-pointer"
                  onClick={() => onViewMessage(log)}
                >
                  <div className="flex items-center gap-4">
                    <StatusBadge status={log.status} />
                    <div>
                      <p className="text-sm text-white">{log.report_type}</p>
                      <p className="text-xs text-zinc-500">{log.report_period_start} - {log.report_period_end}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-300">{formatDateTime(log.sent_at || log.created_at)}</p>
                    {log.total_spend > 0 && (
                      <p className="text-xs text-zinc-500">{formatCurrency(log.total_spend)}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-white/10">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Report Config Components ──────────────────────────────────────────────

const FREQ_OPTIONS = [
  { value: '3day',   label: 'Every 3 Days' },
  { value: '15day',  label: '15-Day Review' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'monthly',label: 'Monthly' },
  { value: 'daily',  label: 'Daily' },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: 'leads',    label: 'Leads',         icon: '📋' },
  { value: 'messages', label: 'Conversations',  icon: '💬' },
  { value: 'sales',    label: 'Purchases',      icon: '🛒' },
];

// Live message preview matching backend format functions
const MessagePreview = ({ clientName, accountType, frequency, cityBreakdown, benchmarkCpl }) => {
  const name   = clientName || 'Client';
  const icon   = accountType === 'messages' ? '💬' : '📋';
  const rlabel = accountType === 'messages' ? 'Conversations' : accountType === 'sales' ? 'Purchases' : 'Leads';
  const runit  = rlabel.slice(0, -1).toLowerCase();
  const sSpend = '₹8.2K'; const sResults = 47; const sCpr = '₹175';
  const today  = new Date(); const dd = (d) => d.toLocaleDateString('en-IN', { month: 'short', day: 'numeric' });

  const cityRows = cityBreakdown
    ? ['Chennai      23 chats   ₹155 each ✅', 'Bangalore    14 chats   ₹198 each ✅', 'Coimbatore   10 chats   ₹210 each ⚠️']
    : [];

  let lines = [];
  if (frequency === '3day') {
    const end = new Date(today); end.setDate(end.getDate() - 1);
    const start = new Date(today); start.setDate(start.getDate() - 3);
    lines = [`📊 ${name} — 3 Day Update (${dd(start)}–${dd(end)})`, '',
      `${icon} ${rlabel}: ${sResults}`, `💰 Spent: ${sSpend}`, `📍 Per ${runit}: ${sCpr}`,
      ...(cityRows.length ? ['', ...cityRows] : []), '', '— Profitcast'];
  } else if (frequency === '15day') {
    const m = today.toLocaleDateString('en-IN', { month: 'long' });
    lines = [`📊 ${name} — Mid-Month Review (${m} 1–15, ${today.getFullYear()})`, '',
      `${icon} ${rlabel}: ${sResults * 10}`, `💰 Spent: ₹65.5K`, `📍 Per ${runit}: ${sCpr}`,
      ...(cityRows.length ? ['', ...cityRows] : []), '', 'vs previous 15 days:',
      `${rlabel}: +18% ✅`, `Cost/${runit}: ₹195 → ${sCpr} ✅`, '', '— Profitcast'];
  } else if (frequency === 'monthly') {
    const last = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const lm   = last.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
    lines = [`📊 ${name} — ${lm} Summary`, '',
      `${icon} ${rlabel}: ${sResults * 30}`, `💰 Total spent: ₹2.4L`, `📍 Per ${runit}: ${sCpr}`,
      ...(cityRows.length ? ['', ...cityRows] : []), '', 'vs last month:',
      `${rlabel}: +22% 📈`, `Cost/${runit}: ₹201 → ${sCpr} ✅`, '', '— Profitcast'];
  } else {
    lines = [`📊 ${name} — Last 7 Days`, '',
      `${icon} ${rlabel}: ${sResults}`, `💰 Spent: ${sSpend}`, `📍 Per ${runit}: ${sCpr}`,
      ...(cityRows.length ? ['', ...cityRows] : []), '', '— Profitcast'];
  }

  return (
    <div className="bg-black/40 rounded-xl border border-white/10 p-4">
      <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-3 flex items-center gap-1.5">
        <Eye className="w-3 h-3" /> Message Preview
      </p>
      <div className="bg-[#1a1a2e]/60 rounded-lg p-3 border border-[#6366f1]/20">
        <div className="flex items-start gap-2">
          <div className="w-7 h-7 rounded-full bg-[#6366f1]/30 flex items-center justify-center shrink-0 mt-0.5">
            <Send className="w-3 h-3 text-[#6366f1]" />
          </div>
          <div className="flex-1 font-mono text-xs text-zinc-300 leading-relaxed whitespace-pre-wrap">
            {lines.map((l, i) => (
              <div key={i} className={l === '' ? 'h-2' : l.startsWith('📊') ? 'text-white font-semibold' : ''}>
                {l || '\u00A0'}
              </div>
            ))}
          </div>
        </div>
      </div>
      {benchmarkCpl && (
        <p className="text-[10px] text-zinc-500 mt-2">
          Target CPL: ₹{benchmarkCpl} — ✅ = at/below target · ⚠️ = 20%+ above target
        </p>
      )}
    </div>
  );
};

// Compact toggle switch
const Toggle = ({ checked, onChange, label }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    className="flex items-center justify-between w-full group"
  >
    <span className="text-sm text-zinc-300 group-hover:text-white transition-colors">{label}</span>
    <div className={`relative w-11 h-6 rounded-full transition-colors duration-200 ${checked ? 'bg-[#22c55e]/80' : 'bg-zinc-700'}`}>
      <div className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-200 ${checked ? 'translate-x-5' : 'translate-x-0'}`} />
    </div>
  </button>
);

// Individual report config card
const ReportConfigCard = ({ config, onEdit, onDelete, onSend }) => {
  const freq = FREQ_OPTIONS.find(f => f.value === config.report_frequency)?.label || config.report_frequency;
  const atype = ACCOUNT_TYPE_OPTIONS.find(t => t.value === config.account_type);
  const recipientCount = config.recipients?.length || 0;

  return (
    <div className="bg-[#09090B] rounded-xl border border-white/10 hover:border-[#6366f1]/30 transition-colors p-4 sm:p-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <h4 className="font-semibold text-white text-sm sm:text-base truncate">
              {config.client_name || config.name}
            </h4>
            <StatusBadge status={config.is_enabled ? 'enabled' : 'disabled'} />
            {config.last_report_status && (
              <StatusBadge status={config.last_report_status} />
            )}
          </div>
          <p className="text-zinc-500 text-xs mb-3 truncate">{config.name}</p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
            <div>
              <p className="text-zinc-500 mb-0.5">Frequency</p>
              <p className="text-white font-medium">{freq}</p>
            </div>
            <div>
              <p className="text-zinc-500 mb-0.5">Type</p>
              <p className="text-white font-medium">{atype ? `${atype.icon} ${atype.label}` : '—'}</p>
            </div>
            <div>
              <p className="text-zinc-500 mb-0.5">Recipients</p>
              <p className="text-white font-medium">{recipientCount}</p>
            </div>
            <div>
              <p className="text-zinc-500 mb-0.5">Last Sent</p>
              <p className="text-white font-medium font-mono text-[10px] opacity-80">
                {config.last_sent_at ? new Date(config.last_sent_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }) : 'Never'}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3 mt-3 text-[10px] text-zinc-500">
            {config.city_breakdown && <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-[#22c55e]" /> City breakdown</span>}
            {config.lead_sheet_notify && <span className="flex items-center gap-1"><Bell className="w-3 h-3 text-[#6366f1]" /> Sheet notify</span>}
            {config.benchmark_cpl && <span>Target CPL: ₹{config.benchmark_cpl}</span>}
          </div>
        </div>

        <div className="flex flex-col gap-2 shrink-0">
          <button onClick={() => onEdit(config)}
            className="p-2 bg-zinc-700/50 hover:bg-zinc-700 rounded-lg transition-colors" title="Edit">
            <Settings className="w-4 h-4 text-zinc-300" />
          </button>
          <button onClick={() => onSend(config)}
            className="p-2 bg-[#6366f1]/20 hover:bg-[#6366f1]/30 rounded-lg transition-colors" title="Send now">
            <Send className="w-4 h-4 text-[#6366f1]" />
          </button>
          <button onClick={() => onDelete(config.id)}
            className="p-2 bg-red-500/10 hover:bg-red-500/20 rounded-lg transition-colors" title="Delete">
            <Trash2 className="w-4 h-4 text-red-400" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Full report config create/edit modal
const ReportConfigModal = ({ config, accounts, sheetConfigs, onClose, onSave }) => {
  const isEdit = !!config?.id;
  const [tab, setTab]               = useState('setup');
  const [name, setName]             = useState(config?.name || '');
  const [clientName, setClientName] = useState(config?.client_name || '');
  const [accountId, setAccountId]   = useState(config?.account_id || '');
  const [accountSearch, setAccountSearch] = useState('');
  const [accountType, setAccountType] = useState(config?.account_type || 'leads');
  const [frequency, setFrequency]   = useState(config?.report_frequency || '3day');
  const [reportTime, setReportTime] = useState(config?.report_time || '09:00');
  const [benchmarkCpl, setBenchmarkCpl] = useState(config?.benchmark_cpl || '');
  const [cityBreakdown, setCityBreakdown] = useState(config?.city_breakdown || false);
  const [leadSheetNotify, setLeadSheetNotify] = useState(config?.lead_sheet_notify || false);
  const [sheetUrl, setSheetUrl]     = useState(config?.sheet_url || '');
  const [recipients, setRecipients] = useState(config?.recipients || []);
  const [newRName, setNewRName]     = useState('');
  const [newRChatId, setNewRChatId] = useState('');
  const [saving, setSaving]         = useState(false);

  // Auto-populate sheet URL when account changes
  useEffect(() => {
    if (accountId) {
      const sc = sheetConfigs.find(s => s.account_id === accountId);
      if (sc?.spreadsheet_url) setSheetUrl(sc.spreadsheet_url);
    }
  }, [accountId, sheetConfigs]);

  // Auto-set name from client name
  useEffect(() => {
    if (clientName && !isEdit) setName(`${clientName} — ${FREQ_OPTIONS.find(f => f.value === frequency)?.label || frequency}`);
  }, [clientName, frequency, isEdit]);

  const addRecipient = () => {
    if (!newRName || !newRChatId) return;
    setRecipients(prev => [...prev, { name: newRName, chat_id: newRChatId, role: 'client', is_active: true }]);
    setNewRName(''); setNewRChatId('');
  };

  const removeRecipient = (i) => setRecipients(prev => prev.filter((_, idx) => idx !== i));

  const handleSave = async () => {
    if (!name) return;
    setSaving(true);
    await onSave({
      id: config?.id,
      name, client_name: clientName, account_id: accountId || null,
      account_type: accountType, report_frequency: frequency,
      report_time: reportTime, benchmark_cpl: benchmarkCpl ? parseFloat(benchmarkCpl) : null,
      city_breakdown: cityBreakdown, lead_sheet_notify: leadSheetNotify,
      sheet_url: sheetUrl || null, recipients,
    });
    setSaving(false);
  };

  const TABS = [
    { id: 'setup',      label: 'Setup',      icon: <Settings className="w-3.5 h-3.5" /> },
    { id: 'schedule',   label: 'Schedule',   icon: <RefreshCw className="w-3.5 h-3.5" /> },
    { id: 'message',    label: 'Message',    icon: <MessageSquare className="w-3.5 h-3.5" /> },
    { id: 'sheet',      label: 'Lead Sheet', icon: <Link className="w-3.5 h-3.5" /> },
    { id: 'recipients', label: 'Recipients', icon: <Users className="w-3.5 h-3.5" /> },
  ];

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-[#09090B] rounded-2xl border border-white/10 w-full max-w-2xl my-auto">

        {/* Header */}
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-bold text-white">{isEdit ? 'Edit Report Config' : 'New Report Config'}</h3>
            <p className="text-zinc-500 text-xs mt-0.5">Configure what gets sent, when, and to whom</p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-zinc-800 rounded-lg transition-colors">
            <span className="text-zinc-400 text-lg leading-none">✕</span>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 px-5 pt-4 overflow-x-auto">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold whitespace-nowrap transition-colors ${
                tab === t.id
                  ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30'
                  : 'text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800/50'
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <div className="p-5 grid grid-cols-1 lg:grid-cols-2 gap-5">

          {/* Left: form */}
          <div className="space-y-4">

            {/* SETUP TAB */}
            {tab === 'setup' && (
              <>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Client Display Name <span className="text-zinc-600">(shown in message)</span></label>
                  <input value={clientName} onChange={e => setClientName(e.target.value)}
                    placeholder="e.g. ESC, Aavaranaa"
                    className="w-full px-3 py-2.5 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Config Name <span className="text-zinc-600">(internal label)</span></label>
                  <input value={name} onChange={e => setName(e.target.value)}
                    placeholder="e.g. ESC — Every 3 Days"
                    className="w-full px-3 py-2.5 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none" />
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Account <span className="text-zinc-600">(optional — leave blank for portfolio-wide)</span></label>
                  <div className="relative mb-1.5">
                    <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
                    <input
                      value={accountSearch}
                      onChange={e => setAccountSearch(e.target.value)}
                      placeholder="Search accounts..."
                      className="w-full pl-9 pr-3 py-2 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none"
                    />
                  </div>
                  <select value={accountId} onChange={e => setAccountId(e.target.value)}
                    className="w-full px-3 py-2.5 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#6366f1]/50 focus:outline-none">
                    <option value="">— All accounts —</option>
                    {accounts
                      .filter(a => !accountSearch || a.name.toLowerCase().includes(accountSearch.toLowerCase()))
                      .map(a => (
                        <option key={a.id} value={a.id}>[{a.platform === 'META' ? 'Meta' : 'Google'}] {a.name}</option>
                      ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-2">Account Type <span className="text-zinc-600">(drives the primary metric)</span></label>
                  <div className="flex gap-2">
                    {ACCOUNT_TYPE_OPTIONS.map(o => (
                      <button key={o.value} type="button" onClick={() => setAccountType(o.value)}
                        className={`flex-1 py-2.5 rounded-lg text-xs font-semibold transition-colors border ${
                          accountType === o.value
                            ? 'bg-[#6366f1]/20 text-[#6366f1] border-[#6366f1]/40'
                            : 'bg-zinc-800/50 text-zinc-400 border-white/10 hover:border-zinc-600'
                        }`}>
                        {o.icon} {o.label}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {/* SCHEDULE TAB */}
            {tab === 'schedule' && (
              <>
                <div>
                  <label className="block text-xs text-zinc-400 mb-2">Report Frequency</label>
                  <div className="grid grid-cols-2 gap-2">
                    {FREQ_OPTIONS.map(o => (
                      <button key={o.value} type="button" onClick={() => setFrequency(o.value)}
                        className={`py-2.5 rounded-lg text-xs font-semibold transition-colors border ${
                          frequency === o.value
                            ? 'bg-[#6366f1]/20 text-[#6366f1] border-[#6366f1]/40'
                            : 'bg-zinc-800/50 text-zinc-400 border-white/10 hover:border-zinc-600'
                        }`}>
                        {o.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Send Time</label>
                  <input type="time" value={reportTime} onChange={e => setReportTime(e.target.value)}
                    className="w-full px-3 py-2.5 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm focus:border-[#6366f1]/50 focus:outline-none" />
                  <p className="text-[10px] text-zinc-600 mt-1">Times are in IST (UTC+5:30)</p>
                </div>
              </>
            )}

            {/* MESSAGE TAB */}
            {tab === 'message' && (
              <>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Target CPL / Cost per Result <span className="text-zinc-600">(₹)</span></label>
                  <input type="number" value={benchmarkCpl} onChange={e => setBenchmarkCpl(e.target.value)}
                    placeholder="e.g. 150"
                    className="w-full px-3 py-2.5 bg-zinc-800/50 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none" />
                  <p className="text-[10px] text-zinc-600 mt-1">Campaigns at/below target show ✅, 20%+ above show ⚠️</p>
                </div>
                <div className="space-y-3 bg-zinc-800/30 rounded-xl border border-white/10 p-4">
                  <p className="text-xs text-zinc-400 font-medium">Message Sections</p>
                  <Toggle checked={cityBreakdown} onChange={setCityBreakdown} label="Show campaign/city breakdown" />
                </div>
              </>
            )}

            {/* LEAD SHEET TAB */}
            {tab === 'sheet' && (
              <>
                <div>
                  <label className="block text-xs text-zinc-400 mb-1.5">Google Sheet URL</label>
                  {sheetUrl ? (
                    <div className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/30 border border-[#22c55e]/20 rounded-lg">
                      <Link className="w-4 h-4 text-[#22c55e] shrink-0" />
                      <a href={sheetUrl} target="_blank" rel="noreferrer"
                        className="text-[#22c55e] text-xs truncate hover:underline flex-1">{sheetUrl}</a>
                    </div>
                  ) : (
                    <div className="px-3 py-2.5 bg-zinc-800/30 border border-white/10 rounded-lg text-zinc-500 text-xs">
                      {accountId ? 'No sheet linked to this account yet' : 'Select an account to auto-populate'}
                    </div>
                  )}
                  <p className="text-[10px] text-zinc-600 mt-1">Auto-pulled from the account's lead sheet in Client Portal</p>
                </div>
                <div className="space-y-3 bg-zinc-800/30 rounded-xl border border-white/10 p-4">
                  <Toggle checked={leadSheetNotify} onChange={setLeadSheetNotify}
                    label="Notify when sheet is updated" />
                  <p className="text-[10px] text-zinc-500">Sends a Telegram message after every successful sync</p>
                </div>
              </>
            )}

            {/* RECIPIENTS TAB */}
            {tab === 'recipients' && (
              <>
                {recipients.length > 0 && (
                  <div className="space-y-2">
                    {recipients.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 px-3 py-2.5 bg-zinc-800/30 border border-white/10 rounded-lg">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-white font-medium truncate">{r.name}</p>
                          <p className="text-xs text-zinc-500 font-mono">{r.chat_id}</p>
                        </div>
                        <button onClick={() => removeRecipient(i)}
                          className="p-1.5 hover:bg-red-500/20 rounded transition-colors shrink-0">
                          <Trash2 className="w-3.5 h-3.5 text-red-400" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <div className="space-y-2 bg-zinc-800/30 rounded-xl border border-white/10 p-4">
                  <p className="text-xs text-zinc-400 font-medium mb-3">Add Recipient</p>
                  <input value={newRName} onChange={e => setNewRName(e.target.value)}
                    placeholder="Name (e.g. Ravi)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-lg text-white text-sm placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none" />
                  <input value={newRChatId} onChange={e => setNewRChatId(e.target.value)}
                    placeholder="Telegram Chat ID (e.g. 123456789)"
                    className="w-full px-3 py-2 bg-zinc-900 border border-white/10 rounded-lg text-white text-sm font-mono placeholder-zinc-600 focus:border-[#6366f1]/50 focus:outline-none" />
                  <button onClick={addRecipient} disabled={!newRName || !newRChatId}
                    className="w-full py-2 bg-[#6366f1]/20 text-[#6366f1] rounded-lg text-xs font-semibold hover:bg-[#6366f1]/30 transition-colors disabled:opacity-40 flex items-center justify-center gap-1.5">
                    <Plus className="w-3.5 h-3.5" /> Add Recipient
                  </button>
                  <p className="text-[10px] text-zinc-600">Client must message your bot with /start to get their Chat ID</p>
                </div>
              </>
            )}
          </div>

          {/* Right: live preview */}
          <div className="flex flex-col gap-4">
            <MessagePreview
              clientName={clientName}
              accountType={accountType}
              frequency={frequency}
              cityBreakdown={cityBreakdown}
              benchmarkCpl={benchmarkCpl}
            />
            {/* Quick config summary */}
            <div className="bg-zinc-800/20 rounded-xl border border-white/10 p-4 text-xs space-y-2">
              <p className="text-zinc-400 font-medium mb-2">Config Summary</p>
              {[
                ['Type',      ACCOUNT_TYPE_OPTIONS.find(t => t.value === accountType)?.label || '—'],
                ['Frequency', FREQ_OPTIONS.find(f => f.value === frequency)?.label || '—'],
                ['Send at',   reportTime],
                ['Recipients', recipients.length || '0'],
                ['City rows', cityBreakdown ? 'Yes' : 'No'],
                ['Sheet notify', leadSheetNotify ? 'Yes' : 'No'],
              ].map(([k, v]) => (
                <div key={k} className="flex items-center justify-between">
                  <span className="text-zinc-500">{k}</span>
                  <span className="text-zinc-200 font-medium">{v}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-5 border-t border-white/10 flex items-center justify-between gap-3">
          <div className="flex gap-2">
            {tab !== 'setup' && (
              <button onClick={() => setTab(TABS[TABS.findIndex(t => t.id === tab) - 1]?.id || 'setup')}
                className="px-3 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors">
                ← Back
              </button>
            )}
            {tab !== 'recipients' && (
              <button onClick={() => setTab(TABS[TABS.findIndex(t => t.id === tab) + 1]?.id || 'recipients')}
                className="px-3 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-xs font-semibold hover:bg-zinc-700 transition-colors">
                Next →
              </button>
            )}
          </div>
          <div className="flex gap-3">
            <button onClick={onClose}
              className="px-4 py-2 bg-zinc-700/50 text-zinc-300 rounded-lg text-sm font-semibold hover:bg-zinc-700 transition-colors">
              Cancel
            </button>
            <button onClick={handleSave} disabled={saving || !name}
              className="px-5 py-2 bg-[#6366f1] text-white rounded-lg text-sm font-semibold hover:bg-[#6366f1]/80 transition-colors disabled:opacity-50 flex items-center gap-2">
              {saving ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              {saving ? 'Saving…' : isEdit ? 'Save Changes' : 'Create Config'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Main Component
const TelegramReports = ({ addToast, onNavigate }) => {
  const [activeSection, setActiveSection] = useState('configs');
  const [summary, setSummary] = useState(null);
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [configSearch, setConfigSearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  // Report configs state
  const [reportConfigs, setReportConfigs] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [sheetConfigs, setSheetConfigs] = useState([]);
  const [editingConfig, setEditingConfig] = useState(null);
  const [showNewConfig, setShowNewConfig] = useState(false);

  // Modal states
  const [configureClient, setConfigureClient] = useState(null);
  const [configureTeamMember, setConfigureTeamMember] = useState(null);
  const [sendRecipient, setSendRecipient] = useState(null);
  const [sendType, setSendType] = useState('client');
  const [historyRecipient, setHistoryRecipient] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [selectedLog, setSelectedLog] = useState(null);

  // Fetch data
  const fetchData = useCallback(async () => {
    setLoading(true);
    setLoadError(false);
    try {
      const [summaryRes, clientsRes, teamRes, configsRes, accountsRes, sheetsRes] = await Promise.all([
        fetch(`${API_URL}/telegram/summary`).catch(() => null),
        fetch(`${API_URL}/telegram/clients`).catch(() => null),
        fetch(`${API_URL}/telegram/team`).catch(() => null),
        fetch(`${API_URL}/telegram/reports`).catch(() => null),
        fetch(`${API_URL}/accounts`).catch(() => null),
        fetch(`${API_URL}/sheets/sync-status`).catch(() => null),
      ]);

      if (summaryRes?.ok) setSummary(await summaryRes.json());
      if (clientsRes?.ok) { const d = await clientsRes.json(); setClients(d.clients || []); }
      if (teamRes?.ok)    { const d = await teamRes.json();    setTeamMembers(d.team_members || []); }
      if (configsRes?.ok) { const d = await configsRes.json(); setReportConfigs(d.configs || []); }
      if (accountsRes?.ok){ const d = await accountsRes.json(); setAccounts(d.accounts || d || []); }
      if (sheetsRes?.ok)  { const d = await sheetsRes.json();   setSheetConfigs(d.accounts || []); }
    } catch (error) {
      console.error('Error fetching Telegram data:', error);
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Save client settings
  const handleSaveClientSettings = async (clientId, chatId, frequency, receiveReports) => {
    try {
      const params = new URLSearchParams();
      if (chatId) params.append('telegram_chat_id', chatId);
      if (frequency) params.append('report_frequency', frequency);
      params.append('receive_reports', receiveReports);

      const res = await fetch(`${API_URL}/telegram/clients/${clientId}?${params.toString()}`, {
        method: 'PUT'
      });

      if (res.ok) {
        addToast?.('Client settings saved', 'success');
        fetchData();
        setConfigureClient(null);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving client settings:', error);
      addToast?.('Failed to save settings', 'error');
    }
  };

  // Save team member settings
  const handleSaveTeamMemberSettings = async (memberId, chatId, dailySummary, inactiveAlerts, healthAlerts, weeklySummary) => {
    try {
      const params = new URLSearchParams();
      if (chatId) params.append('telegram_chat_id', chatId);
      params.append('receive_daily_summary', dailySummary);
      params.append('receive_inactive_alerts', inactiveAlerts);
      params.append('receive_health_alerts', healthAlerts);
      params.append('receive_weekly_summary', weeklySummary);

      const res = await fetch(`${API_URL}/telegram/team/${memberId}?${params.toString()}`, {
        method: 'PUT'
      });

      if (res.ok) {
        addToast?.('Team member settings saved', 'success');
        fetchData();
        setConfigureTeamMember(null);
      } else {
        throw new Error('Failed to save settings');
      }
    } catch (error) {
      console.error('Error saving team member settings:', error);
      addToast?.('Failed to save settings', 'error');
    }
  };

  // Report config CRUD
  const handleSaveReportConfig = async (data) => {
    try {
      const { id, recipients: recs, ...body } = data;
      const url    = id ? `${API_URL}/telegram/reports/${id}` : `${API_URL}/telegram/reports`;
      const method = id ? 'PUT' : 'POST';
      const res    = await fetch(url, { method, headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...body, recipients: recs }) });
      if (!res.ok) throw new Error('Save failed');
      addToast?.(id ? 'Report config updated' : 'Report config created', 'success');
      setEditingConfig(null); setShowNewConfig(false);
      fetchData();
    } catch (e) {
      addToast?.('Failed to save report config', 'error');
    }
  };

  const handleDeleteReportConfig = async (id) => {
    if (!window.confirm('Delete this report config?')) return;
    try {
      const res = await fetch(`${API_URL}/telegram/reports/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Delete failed');
      addToast?.('Report config deleted', 'success');
      fetchData();
    } catch (e) {
      addToast?.('Failed to delete', 'error');
    }
  };

  const handleSendReportConfig = async (config) => {
    try {
      const res = await fetch(`${API_URL}/telegram/reports/${config.id}/send`, { method: 'POST' });
      if (!res.ok) throw new Error('Send failed');
      addToast?.(`Report sent for ${config.client_name || config.name}`, 'success');
    } catch (e) {
      addToast?.('Failed to send report', 'error');
    }
  };

  // Send report to client
  const handleSendClientReport = async (clientId, period) => {
    try {
      const res = await fetch(`${API_URL}/telegram/clients/${clientId}/send?period=${period}`, {
        method: 'POST'
      });

      const data = await res.json();
      if (res.ok && data.success) {
        addToast?.(`Report sent to ${data.client_name}! Spend: ${formatCurrency(data.metrics?.spend)}`, 'success');
        fetchData();
        setSendRecipient(null);
      } else {
        throw new Error(data.detail || 'Failed to send report');
      }
    } catch (error) {
      console.error('Error sending client report:', error);
      addToast?.(error.message || 'Failed to send report', 'error');
    }
  };

  // Send report to team member
  const handleSendTeamReport = async (memberId, reportType) => {
    try {
      const res = await fetch(`${API_URL}/telegram/team/${memberId}/send?report_type=${reportType}`, {
        method: 'POST'
      });

      const data = await res.json();
      if (res.ok && data.success) {
        addToast?.(`Report sent to ${data.member_name}!`, 'success');
        fetchData();
        setSendRecipient(null);
      } else {
        throw new Error(data.detail || 'Failed to send report');
      }
    } catch (error) {
      console.error('Error sending team report:', error);
      addToast?.(error.message || 'Failed to send report', 'error');
    }
  };

  // View history
  const handleViewHistory = async (recipient, category) => {
    try {
      // Use chat_id for filtering since that's what we store in logs
      const chatId = recipient.telegram_chat_id;
      const queryParams = new URLSearchParams({
        category: category,
        limit: '20'
      });
      if (chatId) {
        queryParams.append('chat_id', chatId);
      }
      
      const res = await fetch(`${API_URL}/telegram/logs?${queryParams.toString()}`);
      if (res.ok) {
        const data = await res.json();
        setHistoryLogs(data.logs || []);
        setHistoryRecipient(recipient);
      }
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="flex flex-col items-center justify-center h-64 gap-4">
        <div className="w-12 h-12 rounded-2xl bg-red-500/10 flex items-center justify-center">
          <AlertCircle className="w-6 h-6 text-red-400" />
        </div>
        <div className="text-center">
          <p className="text-white font-semibold">Failed to load Telegram data</p>
          <p className="text-zinc-500 text-sm mt-1">Check your connection or server status</p>
        </div>
        <button
          onClick={fetchData}
          className="px-4 py-2 bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30 rounded-lg text-sm font-semibold hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2"
        >
          <RefreshCw className="w-4 h-4" /> Retry
        </button>
      </div>
    );
  }

  const configuredClients = clients.filter(c => c.telegram_chat_id).length;
  const configuredTeam = teamMembers.filter(m => m.telegram_chat_id).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Send className="w-6 h-6 text-[#6366f1]" />
            Telegram Reports
          </h2>
          <p className="text-zinc-400 text-sm mt-1">Send intelligent campaign reports to clients and team members</p>
        </div>
      </div>

      {/* Bot Status */}
      {summary?.bot_info && (
        <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${summary.bot_info.valid ? 'bg-[#22c55e]/10 border border-[#22c55e]/30' : 'bg-red-500/10 border border-red-500/30'}`}>
          <span className={`w-3 h-3 rounded-full ${summary.bot_info.valid ? 'bg-[#22c55e]' : 'bg-red-500'} animate-pulse`} />
          {summary.bot_info.valid ? (
            <span className="text-[#22c55e] text-sm">
              Bot Active: <span className="font-semibold">@{summary.bot_info.bot_username}</span>
            </span>
          ) : (
            <span className="text-red-400 text-sm">
              Bot not configured: {summary.bot_info.error}
            </span>
          )}
        </div>
      )}

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <SummaryCard
          icon={<Users className="w-5 h-5" />}
          title="Clients"
          value={clients.length}
          subtitle={`${configuredClients} configured`}
          color="green"
        />
        <SummaryCard
          icon={<Building2 className="w-5 h-5" />}
          title="Team Members"
          value={teamMembers.length}
          subtitle={`${configuredTeam} configured`}
          color="cyan"
        />
        <SummaryCard
          icon={<CheckCircle className="w-5 h-5" />}
          title="Sent This Week"
          value={summary?.week_reports?.sent || 0}
          subtitle={`${summary?.week_reports?.failed || 0} failed`}
          color={summary?.week_reports?.failed > 0 ? 'yellow' : 'green'}
        />
        <SummaryCard
          icon={<BarChart2 className="w-5 h-5" />}
          title="Total Configs"
          value={(configuredClients + configuredTeam)}
          subtitle="Active recipients"
          color="purple"
        />
      </div>

      {/* Section Tabs */}
      <div className="flex gap-2 border-b border-white/10 pb-2">
        <button
          onClick={() => setActiveSection('clients')}
          className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeSection === 'clients' 
              ? 'bg-[#22c55e]/20 text-[#22c55e] border border-[#22c55e]/30' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <BarChart2 className="w-4 h-4" /> Client Reports
          <span className="bg-zinc-700/50 px-2 py-0.5 rounded text-xs">{clients.length}</span>
        </button>
        <button
          onClick={() => setActiveSection('team')}
          className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeSection === 'team' 
              ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30' 
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <Building2 className="w-4 h-4" /> Internal Team
          <span className="bg-zinc-700/50 px-2 py-0.5 rounded text-xs">{teamMembers.length}</span>
        </button>
        <button
          onClick={() => setActiveSection('configs')}
          className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-colors flex items-center gap-2 ${
            activeSection === 'configs'
              ? 'bg-[#6366f1]/20 text-[#6366f1] border border-[#6366f1]/30'
              : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'
          }`}
        >
          <MessageSquare className="w-4 h-4" /> Report Configs
          <span className="bg-zinc-700/50 px-2 py-0.5 rounded text-xs">{reportConfigs.length}</span>
        </button>
      </div>

      {/* Client Reports Section */}
      {activeSection === 'clients' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Client Reports</h3>
              <p className="text-zinc-400 text-sm">Send campaign performance reports to your clients</p>
            </div>
            {onNavigate && (
              <button
                onClick={() => onNavigate('client-portal')}
                className="px-4 py-2 bg-[#22c55e]/20 text-[#22c55e] rounded-lg text-sm font-semibold hover:bg-[#22c55e]/30 transition-colors flex items-center gap-2"
                data-testid="add-client-btn"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Client
              </button>
            )}
          </div>

          {/* Client Search */}
          {clients.length > 0 && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <input
                value={clientSearch}
                onChange={e => setClientSearch(e.target.value)}
                placeholder="Search clients..."
                className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 pl-9 text-sm text-white placeholder-white/40 w-full focus:outline-none focus:border-white/20"
              />
              {clientSearch && (
                <button onClick={() => setClientSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          )}

          {clients.length === 0 ? (
            <div className="text-center py-12 bg-[#09090B] rounded-xl border border-white/10">
              <div className="flex justify-center mb-3"><Users className="w-8 h-8 text-zinc-500" /></div>
              <p className="text-zinc-400">No clients configured</p>
              <p className="text-zinc-500 text-sm mt-1">Add clients via Client Portal to enable Telegram reports</p>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('client-portal')}
                  className="mt-4 px-6 py-2.5 bg-[#6366f1] text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
                  data-testid="go-to-client-portal-btn"
                >
                  Go to Client Portal
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {clients
                .filter(c => !clientSearch.trim() || (c.name || '').toLowerCase().includes(clientSearch.toLowerCase()))
                .map(client => (
                <ClientCard
                  key={client.id}
                  client={client}
                  onConfigure={setConfigureClient}
                  onSend={(c) => { setSendRecipient(c); setSendType('client'); }}
                  onViewHistory={(c) => handleViewHistory(c, 'client')}
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Internal Team Section */}
      {activeSection === 'team' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Internal Team Reports</h3>
              <p className="text-zinc-400 text-sm">Send alerts and summaries to Profitcast team members</p>
            </div>
            {onNavigate && (
              <button
                onClick={() => onNavigate('team')}
                className="px-4 py-2 bg-[#6366f1]/20 text-[#6366f1] rounded-lg text-sm font-semibold hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2"
                data-testid="add-team-member-btn"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Team Member
              </button>
            )}
          </div>

          {teamMembers.length === 0 ? (
            <div className="text-center py-12 bg-[#09090B] rounded-xl border border-white/10">
              <div className="flex justify-center mb-3"><Building2 className="w-8 h-8 text-zinc-500" /></div>
              <p className="text-zinc-400">No team members configured</p>
              <p className="text-zinc-500 text-sm mt-1">Add team members via Team Management to enable reports</p>
              {onNavigate && (
                <button
                  onClick={() => onNavigate('team')}
                  className="mt-4 px-6 py-2.5 bg-[#6366f1] text-white rounded-lg font-semibold hover:opacity-90 transition-colors"
                  data-testid="go-to-team-management-btn"
                >
                  Go to Team Management
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {teamMembers.map(member => (
                <TeamMemberCard
                  key={member.id}
                  member={member}
                  onConfigure={setConfigureTeamMember}
                  onSend={(m, type) => { setSendRecipient(m); setSendType('team'); }}
                  onViewHistory={(m) => handleViewHistory(m, 'internal')}
                />
              ))}
            </div>
          )}

          {/* Info Box */}
          <div className="bg-zinc-800/30 rounded-xl border border-white/10 p-4 mt-6">
            <h4 className="font-semibold text-white mb-2 flex items-center gap-2"><ClipboardList className="w-4 h-4" /> Report Types Explained</h4>
            <div className="grid grid-cols-2 gap-4 text-sm text-zinc-400">
              <div>
                <p className="font-medium text-zinc-300 mb-1">Weekly Summary</p>
                <p>Performance overview: spend, leads, reach for assigned accounts</p>
              </div>
              <div>
                <p className="font-medium text-zinc-300 mb-1">Inactive Alerts</p>
                <p>Notifies when accounts stop spending (had spend before but not now)</p>
              </div>
              <div>
                <p className="font-medium text-zinc-300 mb-1">Health Alerts</p>
                <p>Critical issues: high CPL, budget exhausted, performance drops</p>
              </div>
              <div>
                <p className="font-medium text-zinc-300 mb-1">Daily Summary</p>
                <p>Brief daily update on account performance (optional)</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Report Configs Section */}
      {activeSection === 'configs' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Report Configs</h3>
              <p className="text-zinc-400 text-sm">Configure what gets sent to each client, when, and how</p>
            </div>
            <button
              onClick={() => setShowNewConfig(true)}
              className="px-4 py-2 bg-[#6366f1]/20 text-[#6366f1] rounded-lg text-sm font-semibold hover:bg-[#6366f1]/30 transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Config
            </button>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <input
              value={configSearch}
              onChange={e => setConfigSearch(e.target.value)}
              placeholder="Search configs by client or name..."
              className="bg-[#09090B] border border-white/10 rounded-lg px-3 py-2 pl-9 text-sm text-white placeholder-white/40 w-full focus:outline-none focus:border-white/20"
            />
            {configSearch && (
              <button onClick={() => setConfigSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-white transition-colors">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {reportConfigs.length === 0 ? (
            <div className="text-center py-16 bg-[#09090B] rounded-xl border border-white/10 border-dashed">
              <MessageSquare className="w-10 h-10 text-zinc-600 mx-auto mb-3" />
              <p className="text-zinc-400 font-medium">No report configs yet</p>
              <p className="text-zinc-500 text-sm mt-1 mb-5">Create a config to start sending intelligent client reports</p>
              <button onClick={() => setShowNewConfig(true)}
                className="px-6 py-2.5 bg-[#6366f1] text-white rounded-lg text-sm font-semibold hover:bg-[#6366f1]/80 transition-colors flex items-center gap-2 mx-auto">
                <Plus className="w-4 h-4" /> Create First Config
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              {reportConfigs
                .filter(c => !configSearch.trim() || (c.client_name || c.name || '').toLowerCase().includes(configSearch.toLowerCase()))
                .map(config => (
                <ReportConfigCard
                  key={config.id}
                  config={config}
                  onEdit={setEditingConfig}
                  onDelete={handleDeleteReportConfig}
                  onSend={handleSendReportConfig}
                />
              ))}
            </div>
          )}

          {/* Cadence reference */}
          <div className="bg-zinc-800/20 rounded-xl border border-white/10 p-4 mt-2">
            <h4 className="text-sm font-semibold text-white mb-3 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 text-[#6366f1]" /> Cadences
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              {[
                ['Every 3 Days', 'Quick pulse — are things working?'],
                ['15-Day Review', 'Fires on 1st and 15th each month'],
                ['Weekly',       'Every 7 days on a chosen day'],
                ['Monthly',      'Full month roundup on the 1st'],
                ['Recharge',     'Auto-triggered when balance < 4 days'],
                ['Lead Sheet',   'Auto-triggered after sheet sync'],
              ].map(([k, v]) => (
                <div key={k} className="bg-zinc-800/40 rounded-lg p-3">
                  <p className="text-zinc-200 font-medium mb-1">{k}</p>
                  <p className="text-zinc-500">{v}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {configureClient && (
        <ConfigureClientModal
          client={configureClient}
          onClose={() => setConfigureClient(null)}
          onSave={handleSaveClientSettings}
        />
      )}

      {configureTeamMember && (
        <ConfigureTeamMemberModal
          member={configureTeamMember}
          onClose={() => setConfigureTeamMember(null)}
          onSave={handleSaveTeamMemberSettings}
        />
      )}

      {sendRecipient && (
        <SendReportModal
          recipient={sendRecipient}
          type={sendType}
          onClose={() => setSendRecipient(null)}
          onSend={sendType === 'client' ? handleSendClientReport : handleSendTeamReport}
        />
      )}

      {historyRecipient && (
        <HistoryModal
          recipient={historyRecipient}
          logs={historyLogs}
          onClose={() => { setHistoryRecipient(null); setHistoryLogs([]); }}
          onViewMessage={setSelectedLog}
        />
      )}

      {selectedLog && (
        <MessagePreviewModal
          log={selectedLog}
          onClose={() => setSelectedLog(null)}
        />
      )}

      {(showNewConfig || editingConfig) && (
        <ReportConfigModal
          config={editingConfig}
          accounts={accounts}
          sheetConfigs={sheetConfigs}
          onClose={() => { setShowNewConfig(false); setEditingConfig(null); }}
          onSave={handleSaveReportConfig}
        />
      )}
    </div>
  );
};

export default TelegramReports;
